import React, { Fragment, useEffect, useRef, useState } from 'react';
import styles from './FaqView.module.scss';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import { SupportContent } from '../../Components/SupportContent/SupportContent';
import { getFaq } from '../../Helpers';
import { AppConfig, Faq } from '../../Models';
import { FaqQuestion } from './FaqQuestion/FaqQuestion';
import { FaqMediaContent } from './FaqMediaContent/FaqMediaContent';
import { FaqHelp } from './FaqHelp/FaqHelp';
import { FaqFeedback } from './FaqFeedback/FaqFeedback';
import { FaqRelated } from './FaqRelated/FaqRelated';
import { FaqDeprecated } from './FaqDeprecated/FaqDeprecated';
import { sendGAThumbEvent, ThumbAction } from '../../Services/AnalyticsService';

function FaqView(): React.ReactElement<{}> {
  const config = useOutletContext<AppConfig>();
  const location = useLocation();
  const { id } = useParams();

  const [showHelp, setShowHelp] = useState<boolean>(false);

  const [selected, setSelected] = useState<ThumbAction | undefined>();

  const faq: Faq | undefined = id ? getFaq(config, +id) : undefined;

  const refHelp = useRef<HTMLDivElement>(null);

  if (!faq) {
    throw Error(`No faq found with the provided id (${id})`);
  }

  const onFeedbackUp = () => {
    setShowHelp(false);
    setSelected('thumb_up');
    sendGAThumbEvent('thumb_up', faq.id);
  };

  const onFeedbackDown = () => {
    setShowHelp(true);
    setSelected('thumb_down');
    sendGAThumbEvent('thumb_down', faq.id);
  };

  useEffect(() => {
    setSelected(undefined);
  }, [location]);

  useEffect(() => {
    if (showHelp) {
      refHelp.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [showHelp]);

  return (
    <SupportContent section="FAQ" title={faq.question}>
      {!faq.active && <FaqDeprecated faq={faq} />}

      {faq.active && (
        <div className={styles.faqView}>
          <FaqQuestion faq={faq} />

          {faq.related_media && (
            <Fragment>
              <hr />
              <FaqMediaContent media={faq.related_media} />
            </Fragment>
          )}

          {faq.related_questions.length > 0 && (
            <Fragment>
              <hr />
              <FaqRelated ids={faq.related_questions} />
            </Fragment>
          )}

          <Fragment>
            <hr />
            <FaqFeedback
              selected={selected}
              onUp={onFeedbackUp}
              onDown={onFeedbackDown}
            />
          </Fragment>

          {showHelp && (
            <Fragment>
              <hr />
              <div ref={refHelp}>
                <FaqHelp url={config.support_link} />
              </div>
            </Fragment>
          )}
        </div>
      )}
    </SupportContent>
  );
}

export default FaqView;
